import * as Hangul from 'hangul-js'
import { makeAutoObservable, runInAction } from 'mobx'

import { studentController } from '@/controller/student.controller'
import type { GradeGroup, LessonClassGroup, StudentGroups, StudentGroupT } from '@/dto'
import { GroupInfo, SchoolGrade, Student, StudentColl } from '@/dto'
import { RouteName } from '@/router'
import { sessionStorageService } from '@/service/storage.service'
import dateUtils from '@/utils/DateUtils'
import { CheckSet } from '@/utils/models'

export type ServiceNameT = (typeof RouteName)['lesson']

export default class StudentGroupAccordionService {
  private _searchText = ''
  studentGroups?: StudentGroups
  sortType: '학년' | '반' = '학년'
  selectedGroupInfo?: GroupInfo
  accordionAllOpened = false // 학년 전체 열기
  classAccordionAllOpened = false // 반 전체 열기

  isAccordionShow = true
  isFloated = false

  // 가장 최근에 선택된 학생의 아코디언 그룹 Id
  latestSelectedAccordionGroupId?: StudentGroupT['id']
  selectedStudentInfo?: { id: Student['id']; name: Student['name'] }

  // 열리고 닫혔는지에 대한 key값 관리
  private _accordionSet = new CheckSet<GroupInfo['key']>()
  private _classAccordionSet = new CheckSet<GroupInfo['key']>()
  serviceName?: ServiceNameT

  constructor(serviceName: ServiceNameT) {
    makeAutoObservable(this)
    this.serviceName = serviceName
    this.sortType = sessionStorageService.getItem(getSortTypeStorageName(serviceName)) ?? '학년'
  }

  private get _studentGroupList(): Array<GradeGroup | LessonClassGroup> {
    if (!this.studentGroups) {
      return []
    }
    return this.sortType === '학년'
      ? this.studentGroups.gradeGroupArr
      : this.studentGroups.lessonClassGroupArr
  }

  get selectedGroup() {
    if (!this.selectedGroupInfo || !this.studentGroups) return

    return this.studentGroups.getGroupFromGroupInfo(this.selectedGroupInfo)
  }

  setIsAccordionShow(visible: boolean) {
    this.isAccordionShow = visible
  }

  setIsFloated(floated: boolean) {
    this.isFloated = floated
  }

  setSelectedGroupInfo(groupInfo?: GroupInfo) {
    this.selectedGroupInfo = groupInfo
  }

  toggleAllAccordionOpened(sortType: StudentGroupAccordionService['sortType']) {
    if (sortType === '학년') {
      if (this.accordionAllOpened) {
        this.studentGroups?.gradeGroupArr?.forEach(() => this._accordionSet.clearAllCheck())
      } else {
        this.studentGroups?.gradeGroupArr?.forEach(({ id }) => this._accordionSet.setCheck(id))
      }
      this.accordionAllOpened = !this.accordionAllOpened
    } else {
      if (this.classAccordionAllOpened) {
        this.studentGroups?.lessonClassGroupArr?.forEach(() =>
          this._classAccordionSet.clearAllCheck(),
        )
      } else {
        this.studentGroups?.lessonClassGroupArr?.forEach(({ id }) =>
          this._classAccordionSet.setCheck(id),
        )
      }
      this.classAccordionAllOpened = !this.classAccordionAllOpened
    }
  }
  // 최초 진입 -> url의 학생 넣어줄 때, 아코디언 같이 열어주기 위한 함수
  openAccordionByGroup(studentGroup: GroupInfo) {
    switch (studentGroup.data.type) {
      case 'student': {
        const student = this.studentGroups?.studentColl?.get(studentGroup.data.studentId)
        if (student) {
          this._accordionSet.setCheck(student.schoolGrade.koLabel)
        }
        break
      }
      case 'grade': {
        this._accordionSet.setCheck(studentGroup.key)
        runInAction(() => {
          this.latestSelectedAccordionGroupId = studentGroup.key as StudentGroupT['id']
        })
        break
      }
      case 'lesson-class':
      default: {
        this._classAccordionSet.setCheck(studentGroup.key)
        runInAction(() => {
          this.latestSelectedAccordionGroupId = studentGroup.key as StudentGroupT['id']
        })
      }
    }
  }

  set searchText(searchValue: string) {
    this._searchText = searchValue
  }
  get isSearching() {
    return this._searchText !== ''
  }
  // 학생 검색
  get studentGroupList() {
    if (!this._studentGroupList) return []
    if (this._searchText === '') {
      return this._studentGroupList
    }

    const searcher = new Hangul.Searcher(this._searchText)

    const searchResultStudentList: StudentGroupT[] = []

    for (const studentGroup of this._studentGroupList) {
      const matchedStudentColl = new StudentColl()
      for (const student of studentGroup.students.values()) {
        if (searcher.search(student.name) !== -1) {
          matchedStudentColl.set(student)
        }
      }

      if (matchedStudentColl.size > 0) {
        searchResultStudentList.push({ ...studentGroup, students: matchedStudentColl })
      }
    }

    return searchResultStudentList
  }
  // 학생 아코디언 목록 -> 아코디언 그룹명(학년, 반)을 클릭할 때 toggle하는 함수
  toggleAccordionByGroupId(groupType: StudentGroupT['type'], groupId: StudentGroupT['id']) {
    if (groupType === 'grade') {
      this._accordionSet.toggleCheck(groupId)
      if (this._accordionSet.isFullyChecked || this._accordionSet.isNoChecked) {
        this.accordionAllOpened = !this.accordionAllOpened
      }
    } else {
      this._classAccordionSet.toggleCheck(groupId)
      if (this._classAccordionSet.isFullyChecked || this._classAccordionSet.isNoChecked) {
        this.classAccordionAllOpened = !this.classAccordionAllOpened
      }
    }
  }

  isOpenGroupId(groupType: StudentGroupT['type'], groupId: StudentGroupT['id']) {
    return groupType === 'grade'
      ? this._accordionSet.isChecked(groupId)
      : this._classAccordionSet.isChecked(groupId)
  }

  changeSortType(sortType: '학년' | '반') {
    this.sortType = sortType
    sessionStorageService.setItem(getSortTypeStorageName(this.serviceName!))(sortType)
  }

  handleSelectGroup(studentGroup: StudentGroupT | Student) {
    this.selectGroup(studentGroup)
  }

  // 체크의 기준이 되는 allList 생성
  private setAccordionCheckedList() {
    const grades =
      this.studentGroups?.gradeGroupArr?.filter(({ students }) => students.isNotEmpty) || []
    const lessonClasses =
      this.studentGroups?.lessonClassGroupArr?.filter(
        (lessonClass) => lessonClass.students.isNotEmpty,
      ) || []

    runInAction(() => {
      this._accordionSet.allList = grades.map(({ name }) => name)
      this._classAccordionSet.allList = lessonClasses.map(({ name }) => name)
    })
  }

  isFetched = false

  async initStudentGroup() {
    await this.fetchUpdateStudentGroups()
    this.setAccordionCheckedList()
  }

  async fetchUpdateStudentGroups() {
    const studentGroups = await studentController.fetchStudentGroups({ serviceType: 'SCIENCEFLAT' })

    // 아코디언 블로킹 방지 -> 학생 그룹이 있을 때 `학생 그룹 아코디언`이 노출되기 때문에
    this.studentGroups = studentGroups
    this.isFetched = true
  }

  async getUrlGroupInfoFromPath(
    pathData:
      | {
          tab: string
          groupType: string
          key: string
        }
      | undefined,
  ) {
    if (!pathData?.tab || !pathData?.groupType || !pathData?.key) return

    const groupInfo = (() => {
      switch (pathData?.groupType) {
        case 'grade': {
          const schoolGrade = SchoolGrade.fromKoLabel(
            pathData.key as (typeof SchoolGrade.KO_LABEL)[number],
          )

          if (schoolGrade) {
            return new GroupInfo({
              type: 'grade',
              schoolGrade,
            })
          }
          break
        }
        case 'lesson-class': {
          const studentLessonClass = this.studentGroups?.studentsByLessonClass?.get(
            Number(pathData.key),
          )

          if (studentLessonClass) {
            return new GroupInfo({
              type: 'lesson-class',
              classId: studentLessonClass.id,
              name: studentLessonClass.name ?? '',
            })
          }
          break
        }
        case 'student': {
          const student = this.studentGroups?.getStudent(pathData.key as Student['id'])
          if (student) {
            return new GroupInfo({
              type: 'student',
              studentId: student.id,
              name: student.name,
            })
          }
          break
        }
      }
    })()

    return groupInfo
  }

  // 학생리스트에서 선택시
  private selectGroup(studentGroup: StudentGroupT | Student) {
    const groupInfo = (() => {
      if (studentGroup instanceof Student) {
        return GroupInfo.fromStudent(studentGroup)
      }
      return GroupInfo.fromStudentGroup(studentGroup)
    })()

    this.setSelectedGroupInfo(groupInfo)
  }
}

const getSortTypeStorageName = (_serviceName: ServiceNameT) => {
  return '수업_아코디언_학생타입' as const
}
